import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';
import './Warden.css';

const empty = { name: '', title: 'Hostel Warden', contactNo: '', whatsappNo: '', email: '', officeLocation: '', availableHours: '', message: '', photo: null };
const phoneInput = value => String(value || '').replace(/\D/g, '').slice(0, 11);

export default function Warden() {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/warden').then(response => {
      setForm(response.data);
      setPreview(response.data.photo || '');
    }).catch(err => setError(err.response?.data?.message || 'Could not load warden details.')).finally(() => setLoading(false));
  }, []);

  useEffect(() => () => { if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

  const choosePhoto = event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type)) return setError('Please select a JPG, PNG, WebP or AVIF photo.');
    if (file.size > 5 * 1024 * 1024) return setError('Warden photo must be under 5 MB.');
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    setPhoto(file); setPreview(URL.createObjectURL(file)); setError('');
  };

  const save = async event => {
    event.preventDefault(); setSaving(true); setError(''); setMessage('');
    try {
      const data = new FormData();
      ['name', 'title', 'contactNo', 'whatsappNo', 'email', 'officeLocation', 'availableHours', 'message'].forEach(key => data.append(key, form[key] || ''));
      if (photo) data.append('photo', photo);
      const response = await api.put('/warden', data);
      setForm(response.data); setPhoto(null); setPreview(response.data.photo || '');
      setMessage('Warden details saved and published to student/staff portals.');
    } catch (err) { setError(err.response?.data?.message || 'Could not save warden details.'); }
    finally { setSaving(false); }
  };

  const removePhoto = async () => {
    if (photo) {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
      setPhoto(null);
      setPreview(form.photo || '');
      setError('');
      setMessage('Selected photo cleared.');
      return;
    }
    setSaving(true); setError('');
    try {
      const response = await api.delete('/warden/photo');
      setForm(response.data); setPhoto(null); setPreview(''); setMessage('Warden photo removed.');
    } catch (err) { setError(err.response?.data?.message || 'Could not remove the photo.'); }
    finally { setSaving(false); }
  };

  if (loading) return <Layout title="Warden Details"><div className="card text-center" style={{ padding: 50 }}>Loading warden details...</div></Layout>;

  return <Layout title="Warden Details">
    <div className="warden-admin-intro card"><div><span>User support contact</span><h2>Warden profile and availability</h2><p>These details are visible only to signed-in students and staff.</p></div></div>
    {error && <div className="alert alert-error">{error}</div>}{message && <div className="alert alert-success">{message}</div>}
    <form className="card warden-admin-form" onSubmit={save}>
      <section className="warden-photo-editor">
        <div className="warden-photo-preview">{preview ? <img src={preview} alt="Warden preview" /> : <span>W</span>}</div>
        <div><h3>Warden photo</h3><p>Upload an existing image or use a mobile/desktop camera.</p><div className="image-source-actions"><label className="btn btn-primary">Upload image<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={choosePhoto} /></label><label className="btn btn-outline">📷 Take photo<input type="file" accept="image/*" capture="user" onChange={choosePhoto} /></label>{preview && <button type="button" className="btn btn-outline" onClick={removePhoto} disabled={saving}>{photo ? 'Clear selection' : 'Remove photo'}</button>}</div><small>Maximum 5 MB. Portrait image recommended.</small></div>
      </section>
      <div className="form-grid">
        <div className="form-group"><label>Full name *</label><input value={form.name} maxLength={100} onChange={event => setForm({ ...form, name: event.target.value })} required /></div>
        <div className="form-group"><label>Title *</label><input value={form.title} maxLength={100} onChange={event => setForm({ ...form, title: event.target.value })} required placeholder="Hostel Warden" /></div>
        <div className="form-group"><label>Contact number</label><input value={form.contactNo} inputMode="numeric" pattern="03[0-9]{9}" maxLength={11} onChange={event => setForm({ ...form, contactNo: phoneInput(event.target.value) })} placeholder="03XXXXXXXXX" /><small>11 digits, starting with 03</small></div>
        <div className="form-group"><label>WhatsApp number</label><input value={form.whatsappNo} inputMode="numeric" pattern="03[0-9]{9}" maxLength={11} onChange={event => setForm({ ...form, whatsappNo: phoneInput(event.target.value) })} placeholder="03XXXXXXXXX" /><small>11 digits, starting with 03</small></div>
        <div className="form-group"><label>Email</label><input type="email" value={form.email} maxLength={150} onChange={event => setForm({ ...form, email: event.target.value })} placeholder="warden@example.com" /></div>
        <div className="form-group"><label>Office location</label><input value={form.officeLocation} maxLength={150} onChange={event => setForm({ ...form, officeLocation: event.target.value })} placeholder="Ground floor, Warden Office" /></div>
        <div className="form-group room-wide-field"><label>Available hours</label><input value={form.availableHours} maxLength={150} onChange={event => setForm({ ...form, availableHours: event.target.value })} placeholder="Daily, 9:00 AM – 10:00 PM" /></div>
        <div className="form-group room-wide-field"><label>Message for users</label><textarea rows={4} maxLength={1000} value={form.message} onChange={event => setForm({ ...form, message: event.target.value })} placeholder="How and when residents should contact the warden" /></div>
      </div>
      <div className="form-actions"><button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save and publish details'}</button></div>
    </form>
  </Layout>;
}
